import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import { Link } from 'react-router-dom';

// Import icons from react-icons/fa
import {
    FaShoppingBag,           // Ecommerce
    FaHotel,                 // Hospitality
    FaPlane,                 // Travel
    FaBuilding,              // B2B
    FaChartLine,             // Financial Services
    FaCloud,                 // SaaS
    FaCreditCard,            // Fintech
    FaGlobe,                 // All sectors
    FaQuoteLeft,
    FaQuoteRight,
    FaArrowRight
} from 'react-icons/fa';

const IndustriesSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    // Intersection Observer to trigger animations every time section comes into view
    useEffect(() => {
        const currentSection = sectionRef.current;

        const observer = new IntersectionObserver(
            ([entry]) => {
                // Reset animation when section leaves view
                if (!entry.isIntersecting) {
                    setIsVisible(false);
                } else {
                    // Trigger animation when section enters view
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.2,
                rootMargin: '0px'
            }
        );

        if (currentSection) {
            observer.observe(currentSection);
        }

        return () => {
            if (currentSection) {
                observer.unobserve(currentSection);
            }
        };
    }, []);

    // Industry data with React icons and routes
    const industries = [
        {
            id: 1,
            title: "Ecommerce",
            description: "Scaling online sales through performance marketing & CRO",
            icon: FaShoppingBag,
            path: "/sectors/ecommerce",
            delay: 0.1
        },
        {
            id: 2,
            title: "Hospitality",
            description: "Driving direct bookings & brand visibility",
            icon: FaHotel,
            path: "/sectors/hospitality",
            delay: 0.2
        },
        {
            id: 3,
            title: "Travel",
            description: "Demand generation across paid & organic channels",
            icon: FaPlane,
            path: "/sectors/travel",
            delay: 0.3
        },
        {
            id: 4,
            title: "B2B",
            description: "Lead generation & pipeline growth",
            icon: FaBuilding,
            path: "/sectors/b2b",
            delay: 0.4
        },
        {
            id: 5,
            title: "Financial Services",
            description: "Trust-led marketing with compliance awareness",
            icon: FaChartLine,
            path: "/sectors/finance",
            delay: 0.5
        },
        {
            id: 6,
            title: "SaaS",
            description: "Growth marketing across the full funnel",
            icon: FaCloud,
            path: "/sectors/sass",
            delay: 0.6
        },
        {
            id: 7,
            title: "Fintech",
            description: "Performance, trust & scale in competitive markets",
            icon: FaCreditCard,
            path: "/sectors/fintech",
            delay: 0.7
        },
        {
            id: 8,
            title: "All Sectors",
            description: "Tailored strategies for every business type",
            icon: FaGlobe,
            path: "/sectors/all-sectors",
            delay: 0.8
        }
    ];

    return (
        <div
            className={`rtx-industries-section ${isVisible ? 'rtx-visible' : ''}`}
            ref={sectionRef}
        >
            {/* SVG Gradient Definition */}
            <svg width="0" height="0" style={{ position: 'absolute' }}>
                <linearGradient id="rtx-instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop stopColor="#405de6" offset="0%" />
                    <stop stopColor="#5851db" offset="20%" />
                    <stop stopColor="#833ab4" offset="40%" />
                    <stop stopColor="#c13584" offset="60%" />
                    <stop stopColor="#e1306c" offset="80%" />
                    <stop stopColor="#fd1d1d" offset="100%" />
                </linearGradient>
            </svg>

            <Container>
                {/* New Text Content - replaces original title */}
                <Row className="mb-5">
                    <Col xl='10' className="mx-auto text-center">
                        <div
                            className={`rtx-intro-text text-center ${isVisible ? 'rtx-animated' : ''}`}
                        >
                            <h1 className='title'>
                                At Reachtix, we don't believe in one-size-fits-all strategies.
                            </h1>
                        </div>
                        <Card className='rounded position-relative'>
                            <CardBody>
                                <FaQuoteLeft
                                    style={{
                                        fontSize: '3rem',
                                        color: '#e0bf5c',
                                        opacity: 0.3,
                                        position: 'absolute',
                                        top: '20px',
                                        left: '20px'
                                    }}
                                />
                                <FaQuoteRight
                                    style={{
                                        fontSize: '3rem',
                                        color: '#e0bf5c',
                                        opacity: 0.3,
                                        position: 'absolute',
                                        bottom: '20px',
                                        right: '20px'
                                    }}
                                />
                                <h3 className='subtitle' style={{ position: 'relative', zIndex: 1, paddingLeft: '40px', paddingRight: '40px' }}>
                                    We've built strong, hands-on experience working with different businesses, helping them grow through tailored digital marketing, web design, and performance-driven campaigns.
                                </h3>
                                <h3 className='subtitle' style={{ position: 'relative', zIndex: 1, paddingLeft: '40px', paddingRight: '40px' }}>
                                    Our understanding of each business's unique audience, market, and challenges allows us to create focused strategies that deliver real results — whether you're a local business or looking to scale beyond your current market.
                                </h3>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Industries Grid */}
                <Row className="rtx-g-3">
                    {industries.map((industry) => (
                        <Col
                            key={industry.id}
                            xs={6}
                            sm={6}
                            md={6}
                            lg={3}
                            className="rtx-industry-col"
                        >
                            <Link
                                to={industry.path}
                                className={`rtx-industry-card ${isVisible ? 'rtx-animated' : ''}`}
                                style={{
                                    transitionDelay: isVisible ? `${industry.delay}s` : '0s',
                                    opacity: isVisible ? 1 : 0,
                                    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                                    textDecoration: 'none',
                                    display: 'block'
                                }}
                            >
                                <div className="rtx-industry-icon-wrapper">
                                    <div className={`rtx-industry-icon ${isVisible ? 'rtx-animated' : ''}`}
                                        style={{
                                            transitionDelay: isVisible ? `${industry.delay}s` : '0s',
                                            opacity: isVisible ? 1 : 0,
                                            transform: isVisible ? 'scale(1)' : 'scale(0.8)'
                                        }}>
                                        <industry.icon />
                                    </div>
                                </div>
                                <h3 className={`rtx-industry-title ${isVisible ? 'rtx-animated' : ''}`}
                                    style={{
                                        transitionDelay: isVisible ? `${industry.delay + 0.1}s` : '0s',
                                        opacity: isVisible ? 1 : 0,
                                        transform: isVisible ? 'translateY(0)' : 'translateY(10px)'
                                    }}>
                                    {industry.title}
                                </h3>
                                <p className={`rtx-industry-description ${isVisible ? 'rtx-animated' : ''}`}
                                    style={{
                                        transitionDelay: isVisible ? `${industry.delay + 0.2}s` : '0s',
                                        opacity: isVisible ? 1 : 0,
                                        transform: isVisible ? 'translateY(0)' : 'translateY(10px)'
                                    }}>
                                    {industry.description}
                                </p>
                                <span
                                    className="rtx-learn-more"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        color: '#e0bf5c',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        marginTop: '10px',
                                        transition: 'gap 0.3s ease'
                                    }}
                                >
                                    Learn More <FaArrowRight style={{ fontSize: '0.8rem' }} />
                                </span>
                            </Link>
                        </Col>
                    ))}
                </Row>
            </Container>
        </div>
    );
};

export default IndustriesSection;